import { Table } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { useWeb3React } from '@web3-react/core'
import { getStakingList, getStakingListTotal } from '~/apis'
import { useBreakpoint } from '~/hooks'
import { getExplorerTransactionLink } from '~/utils/chain'
import { formatNumber } from '~/utils/numbers'
import { StyledDepositListContainer, StyledDepositListWrapper } from './styled'
import { StakeType } from './types'

const StakeRecordList: React.FC<{
  page: number
  setPage: (arg: number) => void
}> = ({ page, setPage }) => {
  const { isMobile } = useBreakpoint()
  const { t } = useTranslation()
  const { account, chainId } = useWeb3React()

  const { data: records, isValidating } = useSWR(
    ['getStakingList', chainId, account, page],
    () => getStakingList(chainId, account, page, 10),
  )
  const { data: total } = useSWR(
    ['getStakingListTotal', chainId, account],
    () => getStakingListTotal(chainId, account),
  )

  const columns: any = [
    {
      title: (
        <div className="token-name-head">
          <span className="sort-element">{t('staking_records_col_time')}</span>
        </div>
      ),
      dataIndex: 'redeemTime',
      width: 240,
    },
    {
      title: (
        <div className="buyback-head">
          <span className="sort-element align-right">
            {t('staking_records_col_type')}
          </span>
        </div>
      ),
      dataIndex: 'type',
      align: 'right',
    },
    {
      title: t('staking_records_col_amount'),
      dataIndex: 'tokenAmount',
      align: 'right',
    },
    {
      title: t('staking_records_col_receive'),
      dataIndex: 'receive',
      align: 'right',
    },
  ]

  const renderRedeemTime = (item) => {
    const [_, transactionHash] = item?.transactionHash?.split('-')
    return (
      <a
        href={getExplorerTransactionLink(transactionHash, chainId)}
        target="_blank"
        style={{ fontSize: 16 }}
      >
        <span>{dayjs(item.timestamp * 1000).format('YYYY-MM-DD HH:mm')}</span>
      </a>
    )
  }

  const renderUnLockTime = (item) => {
    if (item.cooldownDate) {
      return <span>{dayjs(item.cooldownDate * 1000).format('YYYY-MM-DD')}</span>
    }
    return null
  }

  const renderTokenAmount = (item) => {
    const isRedeem = item.stakeType === StakeType.Redeem
    if (isRedeem && item.share) {
      return (
        <span style={{ fontSize: 17 }}>
          {formatNumber(item.share, 2, 18)} xLON
        </span>
      )
    }
    if (!isRedeem && item.amount) {
      return (
        <span style={{ fontSize: 17 }}>
          {formatNumber(item.amount, 2, 18)} LON
        </span>
      )
    }
    return '~'
  }

  const renderType = (item) => {
    let style
    let text

    switch (item.stakeType) {
      case StakeType.CoolDown:
        style = 'unlock'
        text = t('unlock')
        break
      case StakeType.Redeem:
        style = 'redeem'
        text = item.penalty ? t('force_redeem') : t('redeem')
        break
      case StakeType.Staked:
        style = 'deposit'
        text = t('stake')
        break
      default:
        break
    }
    return <span className={`${style} action-type`}>{text}</span>
  }

  const renderReceiveAmount = (item) => {
    const isRedeem = item.stakeType === StakeType.Redeem
    if ((isRedeem && !item.amount) || (!isRedeem && !item.share)) {
      return '~'
    }
    let receivedAmount = isRedeem
      ? `${formatNumber(item.amount, 2, 18)} LON`
      : `${formatNumber(item.share, 2, 18)} xLON`
    let penaltyAmount
    if (item.penalty) {
      const penalty = formatNumber(item.penalty, 2, 18)
      penaltyAmount = `(${t('force_redeem_penalty')} ${penalty} LON)`
    }
    return (
      <>
        {item.penalty && (
          <span className="penalty-amount">{penaltyAmount} </span>
        )}
        <span className="receive-amount">{receivedAmount}</span>
      </>
    )
  }

  return (
    <StyledDepositListContainer className="deposit-list-container">
      <section className="record-head">
        <span className="title">{t('staking_records_title')}</span>
        <p className="sub-title">
          <span className="tips">{t('staking_records_title_tips')}</span>
        </p>
      </section>
      <StyledDepositListWrapper>
        <Table
          rowKey="id"
          columns={columns}
          loading={isValidating}
          dataSource={records?.map((item) => ({
            id: item.id,
            redeemTime: renderRedeemTime(item),
            unLockTime: renderUnLockTime(item),
            type: renderType(item),
            tokenAmount: renderTokenAmount(item),
            receive: renderReceiveAmount(item),
          }))}
          showSorterTooltip={false}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            size: isMobile ? 'small' : 'default',
            total,
            onChange: (pageIndex) => setPage(pageIndex),
          }}
          locale={{ emptyText: t('no_data') }}
          showHeader={!!records?.length}
          scroll={!!records?.length ? { x: true } : null}
        />
      </StyledDepositListWrapper>
    </StyledDepositListContainer>
  )
}

export default StakeRecordList
